import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PagePaths } from "@shared/constants";
import { isRecaptchaTokenError } from "@shared/utils/recaptchaTokenHandler";
import { showAuthError as showAuthErrorRegister } from "@features/createaccount/service/RegisterShowError";
import { showAuthError as showAuthErrorOAuth2 } from "@features/authentication/services/OAuth2ShowError";

type SetServerErrorPopUp = React.Dispatch<React.SetStateAction<string | null>>;

const getDirectMessage = (err: any): unknown | null => {
  if (err && err.message) return err.message;
  return null;
};

const getBodyMessage = (err: any): unknown | null => {
  if (err && err.body && err.body.message) return err.body.message;
  return null;
};

const getErrorMessage = (err: any): unknown => {
  return getDirectMessage(err) ?? getBodyMessage(err) ?? '';
};

const extractErrorMessage = (err: any): string => {
  const msg = getErrorMessage(err);
  return typeof msg === 'string' ? msg.toLowerCase() : '';
};

const isFlowTokenError = (err: any) => {
  const lowerCaseMessage = extractErrorMessage(err);
  return lowerCaseMessage.includes('flow token') || lowerCaseMessage.includes('session');
};

const isFlowTokenInvalidation = (error: any): boolean => {
  const is401 = error?.status === 401;
  return is401 && (isFlowTokenError(error) || !isRecaptchaTokenError(error, true));
};

const ANTI_BOT_ERROR_TITLES = [
  "AntiBotTokenMissingError",
  "AntiBotInvalidTokenError",
];

function isAntiBotErrorByTitle(error: any): boolean {
  const title = error?.body?.title;
  return typeof title === 'string' && ANTI_BOT_ERROR_TITLES.includes(title);
}

const isAnyRecaptchaError = (error: any): boolean => {
  return isAntiBotErrorByTitle(error) || isRecaptchaTokenError(error, true) || isRecaptchaTokenError(error, false);
};

const CREDENTIAL_ERROR_TITLES = [
  "InvalidUserCredentialsError",
  "UserNotFoundError",
  "InvalidConfirmationCodeError",
];

function isKnownCredentialError(error: any): boolean {
  const title = error?.body?.title;
  return typeof title === 'string' && CREDENTIAL_ERROR_TITLES.includes(title);
}

type InterceptResult = { path: string; errorMessage?: string } | null;

function interceptCommonErrors(error: any, redirectPath: string, t: (key: string) => string): InterceptResult {
  if (isKnownCredentialError(error)) return null;

  if (isFlowTokenInvalidation(error)) {
    console.warn("[AuthError] Invalid flow token (session expired), restarting flow...");
    return { path: redirectPath };
  }
  if (isAnyRecaptchaError(error)) {
    console.warn("[AuthError] Recaptcha error not recovered, showing user-facing message.");
    return { path: "", errorMessage: t('errors.recaptcha.userFacing') };
  }
  return null;
}

function applyInterceptResult(
  intercepted: InterceptResult,
  navigate: (path: string) => void,
  setServerErrorPopUp: SetServerErrorPopUp
): boolean {
  if (!intercepted) return false;
  if (intercepted.path) navigate(intercepted.path);
  else if (intercepted.errorMessage) setServerErrorPopUp(intercepted.errorMessage);
  else setServerErrorPopUp(null);
  return true;
}

export const useAuthError = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleRegisterError = useCallback(
    (
      error: any,
      setServerErrorPopUp: SetServerErrorPopUp,
      frontExceptionFunction: Function
    ) => {
      const intercepted = interceptCommonErrors(error, PagePaths.SIGNUP.CREATEACCOUNT, t);
      if (applyInterceptResult(intercepted, navigate, setServerErrorPopUp)) return;

      showAuthErrorRegister(error, setServerErrorPopUp, frontExceptionFunction, t);
    },
    [navigate, t]
  );

  const handleOAuth2Error = useCallback(
    (
      type: string,
      error: any,
      setServerErrorPopUp: SetServerErrorPopUp,
      fieldErrors: Set<string>
    ) => {
      const intercepted = interceptCommonErrors(error, PagePaths.LOGIN.IDENTITY, t);
      if (applyInterceptResult(intercepted, navigate, setServerErrorPopUp)) return;

      if (error?.body?.error_code === "EMAIL_NOT_VERIFIED") {
        console.warn("[AuthError] Email not verified, redirecting to verification page...");
        const verificationToken = error.body.verification_token;
        const email = error.body.email;
        if (verificationToken && email) {
          try {
            const expirationTime = Math.floor(Date.now() / 1000) + 300;
            sessionStorage.setItem('isSocialLogin', 'true');
            sessionStorage.setItem('socialLoginToken', JSON.stringify({
              access_token: verificationToken,
              token_type: 'Bearer',
              expiration_time: expirationTime,
            }));
            sessionStorage.setItem('emailNotVerifiedEmail', email);
          } catch (e) {
            // sessionStorage unavailable, continue with navigation
          }
        }
        window.location.href = PagePaths.LOGIN.VERIFYEMAIL + window.location.search;
        return;
      }

      showAuthErrorOAuth2(type, error, setServerErrorPopUp, fieldErrors, t);
    },
    [navigate, t]
  );

  return { handleRegisterError, handleOAuth2Error };
};
